import z from "zod";

const dataSyncErrorTypeValues = [
  "MESSAGE_NOT_FOUND",
  "PRODUCTION_BRANCH_ANONYMIZATION_ATTEMPT",
  "DATABASE_URL_ENVIRONMENT_VARIABLE_NOT_FOUND",
  "BACKUP_NOT_FOUND",
  "DELETE_MAIN_BRANCH_ATTEMPT",
  "BRANCH_DELETION_FAILED",
  "PREVIEW_BRANCH_NOT_FOUND",
  "PORTAL_LINK_RESET_IN_PRODUCTION_ATTEMPT",
  "CLERK_ID_OVERWRITE_IN_PRODUCTION_ATTEMPT",
  "FINANCIAL_DATA_RESET_IN_PRODUCTION_ATTEMPT",
  "PROXY_PHONE_RESET_IN_PRODUCTION_ATTEMPT",
  "BACKUP_TOO_OLD",
  "SCRUB_FAILED_ROLLBACK_INITIATED",
  "SCRUB_FAILED",
  "SCRUB_VERIFICATION_FAILED",
  "NON_ESSENTIAL_STEP_FAILED",
  "PREVIEW_BRANCH_NAME_READ_FAILED",
  "PREVIEW_DOPPLER_WRITE_FAILED",
  "PARENT_BRANCH_READY_TIMEOUT",
  "PARENT_BRANCH_NOT_FOUND",
] as const;

const dataSyncErrorTypeSchema = z.enum(dataSyncErrorTypeValues);
const DataSyncErrorType = dataSyncErrorTypeSchema.enum;
type DataSyncErrorType = z.infer<typeof dataSyncErrorTypeSchema>;

type ErrorDetails = {
  sentryDescription: string;
};

export type DataSyncErrors =
  | {
      errorType: typeof DataSyncErrorType.MESSAGE_NOT_FOUND;
      data: {
        batchIndex: number;
        updateLimit: number;
        messageCount: number;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.PRODUCTION_BRANCH_ANONYMIZATION_ATTEMPT;
      data: {
        branch: string;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.DATABASE_URL_ENVIRONMENT_VARIABLE_NOT_FOUND;
      data: {
        vercelProject: string;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.BACKUP_NOT_FOUND;
      data: {
        branchName: string;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.DELETE_MAIN_BRANCH_ATTEMPT;
      data: {
        branchName: string;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.BRANCH_DELETION_FAILED;
      data: {
        branchName: string;
        status: number;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.PREVIEW_BRANCH_NOT_FOUND;
      data: {
        branchPrefix: string;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.PORTAL_LINK_RESET_IN_PRODUCTION_ATTEMPT;
      data: {
        branch: string;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.CLERK_ID_OVERWRITE_IN_PRODUCTION_ATTEMPT;
      data: {
        branch: string;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.FINANCIAL_DATA_RESET_IN_PRODUCTION_ATTEMPT;
      data: {
        branch: string;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.PROXY_PHONE_RESET_IN_PRODUCTION_ATTEMPT;
      data: {
        branch: string;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.BACKUP_TOO_OLD;
      data: {
        id: string;
        createdAt: string;
        state: string;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.SCRUB_FAILED_ROLLBACK_INITIATED;
      data: {
        newBranch: string;
        previousBranch: string;
        scrubError: string;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.SCRUB_FAILED;
      data: {
        branch: string;
        scrubError: string;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.SCRUB_VERIFICATION_FAILED;
      data: {
        check: string;
        [metric: string]: string | number;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.NON_ESSENTIAL_STEP_FAILED;
      data: {
        step: string;
        error: string;
        statusCode?: number;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.PREVIEW_BRANCH_NAME_READ_FAILED;
      data: {
        error: string;
        statusCode?: number;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.PREVIEW_DOPPLER_WRITE_FAILED;
      data: {
        branch: string;
        error: string;
        statusCode?: number;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.PARENT_BRANCH_READY_TIMEOUT;
      data: {
        branchName: string;
        elapsedMs: number;
        branchState?: string;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.PARENT_BRANCH_NOT_FOUND;
      data: {
        branchName: string;
        elapsedMs: number;
      };
    };

export const dataSyncErrorDetails: Record<DataSyncErrorType, ErrorDetails> = {
  [DataSyncErrorType.MESSAGE_NOT_FOUND]: {
    sentryDescription: "Failed to find message batch during anonymization",
  },
  [DataSyncErrorType.PRODUCTION_BRANCH_ANONYMIZATION_ATTEMPT]: {
    sentryDescription: "Attempted to anonymize data on a production branch",
  },
  [DataSyncErrorType.DATABASE_URL_ENVIRONMENT_VARIABLE_NOT_FOUND]: {
    sentryDescription: "DATABASE_URL environment variable missing in Vercel project",
  },
  [DataSyncErrorType.BACKUP_NOT_FOUND]: {
    sentryDescription: "No successful PlanetScale backup found",
  },
  [DataSyncErrorType.DELETE_MAIN_BRANCH_ATTEMPT]: {
    sentryDescription: "Attempted to delete a main PlanetScale branch",
  },
  [DataSyncErrorType.BRANCH_DELETION_FAILED]: {
    sentryDescription: "PlanetScale branch deletion returned an unexpected status",
  },
  [DataSyncErrorType.PREVIEW_BRANCH_NOT_FOUND]: {
    sentryDescription: "Expected preview branch not found in PlanetScale",
  },
  [DataSyncErrorType.PORTAL_LINK_RESET_IN_PRODUCTION_ATTEMPT]: {
    sentryDescription: "Attempted to reset portal links in production",
  },
  [DataSyncErrorType.CLERK_ID_OVERWRITE_IN_PRODUCTION_ATTEMPT]: {
    sentryDescription: "Attempted to overwrite Clerk IDs in production",
  },
  [DataSyncErrorType.FINANCIAL_DATA_RESET_IN_PRODUCTION_ATTEMPT]: {
    sentryDescription: "Attempted to reset financial data in production",
  },
  [DataSyncErrorType.PROXY_PHONE_RESET_IN_PRODUCTION_ATTEMPT]: {
    sentryDescription: "Attempted to reset proxy phone data in production",
  },
  [DataSyncErrorType.BACKUP_TOO_OLD]: {
    sentryDescription: "Backup is too old, it should be less than a week old",
  },
  [DataSyncErrorType.SCRUB_FAILED_ROLLBACK_INITIATED]: {
    sentryDescription: "Data scrubbing failed, rolling back to previous preview branch",
  },
  [DataSyncErrorType.SCRUB_FAILED]: {
    sentryDescription: "Data scrubbing failed on the new preview branch",
  },
  [DataSyncErrorType.SCRUB_VERIFICATION_FAILED]: {
    sentryDescription: "Scrub verification assertion failed before the preview branch flip",
  },
  [DataSyncErrorType.NON_ESSENTIAL_STEP_FAILED]: {
    sentryDescription: "A non-essential step in the data sync pipeline failed",
  },
  [DataSyncErrorType.PREVIEW_BRANCH_NAME_READ_FAILED]: {
    sentryDescription: "Failed to read the live preview branch name from Doppler",
  },
  [DataSyncErrorType.PREVIEW_DOPPLER_WRITE_FAILED]: {
    sentryDescription: "Failed to write the verified preview branch credentials to Doppler",
  },
  [DataSyncErrorType.PARENT_BRANCH_READY_TIMEOUT]: {
    sentryDescription: "Parent PlanetScale branch never finished initializing before the wait ceiling",
  },
  [DataSyncErrorType.PARENT_BRANCH_NOT_FOUND]: {
    sentryDescription: "Parent PlanetScale branch does not exist",
  },
};
